import React, { useState } from "react";
import { Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import { track } from "../lib/analytics";

/**
 * "Suggest your city's first dog-friendly spot" form — shown on city
 * hubs that have zero curated results (typically templated cities
 * where we haven't seeded data yet). Routes submissions to the existing
 * feedback pipeline so the backend doesn't need a brand-new collection.
 *
 * UX: stays compact in its default state, expands on click.
 */
const SuggestPlaceForm = ({ city }) => {
  const [expanded, setExpanded] = useState(false);
  const [placeName, setPlaceName] = useState("");
  const [category, setCategory] = useState("park");
  const [notes, setNotes] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!placeName.trim()) {
      toast.error("Please add the place name");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/suggest-place", {
        city: city.name,
        state: city.state,
        city_slug: city.slug,
        place_name: placeName.trim(),
        category,
        notes: notes.trim(),
        email: email.trim() || null,
      });
      track("place_suggested", {
        city: city.slug,
        category,
      });
      setSubmitted(true);
      toast.success("Thanks — we'll review it and add it to the map.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="suggest-place-success"
        className="bg-brand-light-green border border-brand-forest/30 rounded-3xl p-6 sm:p-7 text-center"
      >
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-forest text-white mb-3">
          <Check className="w-6 h-6" />
        </span>
        <h3 className="font-heading text-xl font-bold text-brand-ink">
          Thanks for the tip!
        </h3>
        <p className="text-sm text-brand-ink/70 mt-2 max-w-md mx-auto">
          We'll review <span className="font-semibold">{placeName}</span> and add it to {city.name} so other dog parents can find it too.
        </p>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        data-testid="suggest-place-cta"
        onClick={() => setExpanded(true)}
        className="w-full bg-white border-2 border-dashed border-brand-forest/40 hover:border-brand-forest hover:bg-brand-light-green/40 rounded-3xl p-6 sm:p-7 text-left transition-all group"
      >
        <div className="flex items-start gap-4">
          <span className="shrink-0 w-12 h-12 rounded-full bg-brand-light-green text-brand-forest flex items-center justify-center group-hover:bg-brand-forest group-hover:text-white transition-colors">
            <Plus className="w-6 h-6" strokeWidth={2.4} />
          </span>
          <div>
            <div className="font-heading text-lg sm:text-xl font-bold text-brand-ink">
              Know a great dog-friendly spot in {city.name}?
            </div>
            <p className="text-sm text-brand-ink/70 mt-1.5 leading-relaxed">
              Suggest a park, restaurant, vet, groomer, or trail — we'll review it and add it so other local dog parents can find it.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-forest mt-3">
              Suggest a place →
            </span>
          </div>
        </div>
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="suggest-place-form"
      className="bg-white border border-brand-border-warm rounded-3xl p-6 sm:p-7 shadow-warm"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg sm:text-xl font-bold text-brand-ink">
            Suggest a place in {city.name}
          </h3>
          <p className="text-xs text-brand-ink/60 mt-1">
            Real check-ins beat guesswork. We'll review and add it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-xs text-brand-ink/50 hover:text-brand-ink underline"
        >
          Cancel
        </button>
      </div>

      <label className="block text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/60 mb-1.5">
        Place name *
      </label>
      <input
        type="text"
        required
        value={placeName}
        onChange={(e) => setPlaceName(e.target.value)}
        placeholder={`e.g. Bayshore Dog Park, ${city.name}`}
        data-testid="suggest-place-name"
        className="w-full bg-brand-cream border border-brand-border-warm rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-brand-forest"
        maxLength={120}
      />

      <label className="block text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/60 mt-4 mb-1.5">
        Category
      </label>
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        data-testid="suggest-place-category"
        className="w-full bg-brand-cream border border-brand-border-warm rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest"
      >
        {CATEGORIES.map((c) => (
          <option key={c.key} value={c.key}>{c.label}</option>
        ))}
      </select>

      <label className="block text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/60 mt-4 mb-1.5">
        What makes it dog-friendly?
      </label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Off-leash? Water station? Patio with water bowls? Vet specializes in surgery?"
        data-testid="suggest-place-notes"
        rows={3}
        maxLength={1000}
        className="w-full bg-brand-cream border border-brand-border-warm rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest resize-none"
      />

      <label className="block text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/60 mt-4 mb-1.5">
        Your email (optional — for follow-up questions)
      </label>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        data-testid="suggest-place-email"
        className="w-full bg-brand-cream border border-brand-border-warm rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest"
      />

      <button
        type="submit"
        data-testid="suggest-place-submit"
        disabled={submitting || !placeName.trim()}
        className="mt-5 inline-flex items-center gap-2 bg-brand-forest text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-brand-forest-dark transition-colors active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
          </>
        ) : (
          <>Submit suggestion</>
        )}
      </button>
    </form>
  );
};

export default SuggestPlaceForm;
